/**
 * Dual paper horizons — 15m + 5m accounts.
 * Filter persists in localStorage for bets/digest views.
 */

export const HORIZONS = ['15m', '5m']
export const HORIZON_FILTERS = ['15m', '5m', 'all']

export const HORIZON_UNITS = {
  '15m': 'pmbot-paper',
  '5m': 'pmbot-paper-5m',
}

export const HORIZON_LABELS = {
  '15m': '15m',
  '5m': '5m',
  all: 'Все',
}

/** Human titles for Overview metric columns. */
export const HORIZON_TITLES = {
  '15m': '15 минут',
  '5m': '5 минут',
}

const FILTER_KEY = 'pmhub.horizon.filter'
const OPS_UNIT_KEY = 'pmhub.ops.unit'
const JOURNAL_UNIT_KEY = 'pmhub.journal.unit'

/**
 * @param {unknown} raw
 * @returns {'15m'|'5m'|'all'}
 */
export function normalizeHorizonFilter(raw) {
  const h = String(raw || '')
    .trim()
    .toLowerCase()
  if (h === '15m' || h === '15' || h === 'h15') return '15m'
  if (h === '5m' || h === '5' || h === 'h5') return '5m'
  if (h === 'all' || h === '*' || h === 'both') return 'all'
  return 'all'
}

/**
 * @param {unknown} raw
 * @returns {'15m'|'5m'}
 */
export function normalizeHorizonId(raw) {
  const h = normalizeHorizonFilter(raw)
  return h === '5m' ? '5m' : '15m'
}

export function readHorizonFilter() {
  try {
    return normalizeHorizonFilter(localStorage.getItem(FILTER_KEY) || 'all')
  } catch {
    return 'all'
  }
}

export function writeHorizonFilter(filter) {
  try {
    localStorage.setItem(FILTER_KEY, normalizeHorizonFilter(filter))
  } catch {
    /* ignore */
  }
}

export function unitForHorizon(hz) {
  return HORIZON_UNITS[normalizeHorizonId(hz)] || HORIZON_UNITS['15m']
}

export function horizonForUnit(unit) {
  const u = String(unit || '')
  if (u === 'pmbot-paper-5m') return '5m'
  if (u === 'pmbot-live-15m') return 'LIVE 15m'
  return '15m'
}

/**
 * @param {string[]} [allowed]
 */
export function readOpsUnit(allowed) {
  const allow = Array.isArray(allowed) && allowed.length ? allowed : ['pmbot-paper', 'pmbot-paper-5m']
  try {
    const u = localStorage.getItem(OPS_UNIT_KEY)
    if (u && allow.includes(u)) return u
  } catch {
    /* ignore */
  }
  return allow[0]
}

/**
 * @param {string} unit
 * @param {string[]} [allowed]
 */
export function writeOpsUnit(unit, allowed) {
  const allow = Array.isArray(allowed) && allowed.length ? allowed : ['pmbot-paper', 'pmbot-paper-5m']
  try {
    const u = String(unit || '')
    localStorage.setItem(OPS_UNIT_KEY, allow.includes(u) ? u : allow[0])
  } catch {
    /* ignore */
  }
}

/**
 * @param {string[]} [allowed]
 */
export function readJournalUnit(allowed) {
  const allow = Array.isArray(allowed) && allowed.length ? allowed : ['pmbot-paper', 'pmbot-paper-5m']
  try {
    const u = localStorage.getItem(JOURNAL_UNIT_KEY)
    if (u && allow.includes(u)) return u
  } catch {
    /* ignore */
  }
  return allow[0]
}

/**
 * @param {string} unit
 * @param {string[]} [allowed]
 */
export function writeJournalUnit(unit, allowed) {
  const allow = Array.isArray(allowed) && allowed.length ? allowed : ['pmbot-paper', 'pmbot-paper-5m']
  try {
    const u = String(unit || '')
    localStorage.setItem(JOURNAL_UNIT_KEY, allow.includes(u) ? u : allow[0])
  } catch {
    /* ignore */
  }
}

/**
 * Merge server glance with health win stats (full settled history).
 * Prefer glance fields; fill from horizons[].health so SPA works before
 * glance payload includes win_rate.
 * @param {Record<string, unknown> | null} bit
 * @param {Record<string, unknown> | null | undefined} health
 */
function withWinStats(bit, health) {
  if (!bit) return null
  const h = health && typeof health === 'object' ? health : {}
  return {
    ...bit,
    wins: bit.wins != null ? bit.wins : h.wins,
    losses: bit.losses != null ? bit.losses : h.losses,
    win_rate: bit.win_rate != null ? bit.win_rate : h.win_rate,
    resolved_count: bit.resolved_count != null ? bit.resolved_count : h.resolved_count,
  }
}

/**
 * Resolve paper bankroll from horizon pack (health → card → null).
 * @param {Record<string, unknown> | null | undefined} pack
 */
export function bankrollFromHorizonPack(pack) {
  if (!pack || typeof pack !== 'object') return null
  const h = pack.health && typeof pack.health === 'object' ? pack.health : {}
  const fromHealth = h.paper_bankroll
  if (fromHealth && typeof fromHealth === 'object') return fromHealth
  const cards = pack.cards && typeof pack.cards === 'object' ? pack.cards : {}
  const card = cards.paper_balance
  if (!card || typeof card !== 'object') return null
  const raw = card.value
  if (raw == null || raw === '' || raw === '—') return null
  const v = Number(String(raw).replace(',', '.'))
  if (!Number.isFinite(v)) return null
  return { balance: v }
}

/**
 * Extract dual glance from /api/paper payload.
 * @param {Record<string, unknown> | null | undefined} paper
 */
export function glanceFromPaper(paper) {
  if (!paper || typeof paper !== 'object') return { '15m': null, '5m': null }
  const hz = paper.horizons && typeof paper.horizons === 'object' ? paper.horizons : {}
  const healthOf = (id) => {
    const pack = hz[id]
    if (pack && pack.health) return pack.health
    if (id === '15m' && paper.health) return paper.health
    return null
  }
  if (paper.glance && typeof paper.glance === 'object') {
    const mergeGlance = (id) => {
      const bit = paper.glance[id] || null
      const health = healthOf(id)
      const fromBit =
        bit && bit.bankroll && typeof bit.bankroll === 'object' && bit.bankroll.balance != null
          ? bit.bankroll
          : null
      const bankroll = fromBit || bankrollFromHorizonPack(horizonPack(paper, id))
      if (!bit && !bankroll) return withWinStats(null, health)
      return withWinStats({ ...(bit || {}), bankroll: bankroll || bit?.bankroll || null }, health)
    }
    return {
      '15m': mergeGlance('15m'),
      '5m': mergeGlance('5m'),
    }
  }
  const bit = (id) => {
    const pack = hz[id]
    if (!pack) {
      // Legacy single-horizon payload ≈ 15m
      if (id === '15m' && paper.health) {
        return withWinStats(
          {
            unit: HORIZON_UNITS['15m'],
            bankroll: paper.health.paper_bankroll || null,
            kill_state: paper.health.kill_state || null,
            kill_status: paper.health.status || null,
            bet_count: paper.health.bet_count,
            paper_pnl_est_sum: paper.health.paper_pnl_est_sum,
            diagnosis: (paper.health.diagnosis && paper.health.diagnosis.label_ru) || null,
          },
          paper.health,
        )
      }
      return null
    }
    const h = pack.health || {}
    const st = pack.stats || {}
    return withWinStats(
      {
        unit: pack.unit || HORIZON_UNITS[id],
        bankroll: bankrollFromHorizonPack(pack) || h.paper_bankroll || null,
        kill_state: h.kill_state || null,
        kill_status: h.status || null,
        bet_count: h.bet_count != null ? h.bet_count : st.paper_bet,
        paper_pnl_est_sum: h.paper_pnl_est_sum,
        diagnosis: (h.diagnosis && h.diagnosis.label_ru) || null,
      },
      h,
    )
  }
  return { '15m': bit('15m'), '5m': bit('5m') }
}

/**
 * @param {Record<string, unknown> | null | undefined} paper
 * @param {'15m'|'5m'} id
 */
export function horizonPack(paper, id) {
  if (!paper) return null
  if (paper.horizons && paper.horizons[id]) return paper.horizons[id]
  if (id === '15m') {
    return {
      id: '15m',
      label: '15m',
      unit: 'pmbot-paper',
      cards: paper.cards || {},
      health: paper.health || null,
      feed: paper.feed || null,
      stats: paper.stats || null,
      meta: paper.meta || null,
    }
  }
  return null
}

/** @param {Record<string, unknown> | null | undefined} paper */
export function bothHorizons(paper) {
  return {
    '15m': horizonPack(paper, '15m'),
    '5m': horizonPack(paper, '5m'),
  }
}

/**
 * Flat glance numbers for DualAccounts UI.
 * @param {Record<string, unknown> | null | undefined} health
 */
export function accountGlance(health) {
  if (!health || typeof health !== 'object') {
    return {
      balance: null,
      today_pnl: null,
      open_stake: null,
      pending: null,
      bets: null,
      pnl_est: null,
      wins: null,
      losses: null,
      win_rate: null,
      resolved_count: null,
      kill_phase: null,
      kill_blocked: false,
      diagnosis: null,
    }
  }
  const bank =
    health.paper_bankroll && typeof health.paper_bankroll === 'object'
      ? health.paper_bankroll
      : {}
  const ks =
    health.kill_state && typeof health.kill_state === 'object' ? health.kill_state : {}
  const diag =
    health.diagnosis && typeof health.diagnosis === 'object' ? health.diagnosis : {}
  return {
    balance: bank.balance != null ? Number(bank.balance) : null,
    today_pnl: bank.today_pnl != null ? Number(bank.today_pnl) : null,
    open_stake: bank.open_stake != null ? Number(bank.open_stake) : null,
    pending: bank.pending != null ? Number(bank.pending) : null,
    bets: health.bet_count != null ? Number(health.bet_count) : null,
    pnl_est: health.paper_pnl_est_sum != null ? Number(health.paper_pnl_est_sum) : null,
    wins: health.wins != null ? Number(health.wins) : null,
    losses: health.losses != null ? Number(health.losses) : null,
    win_rate: health.win_rate != null ? Number(health.win_rate) : null,
    resolved_count: health.resolved_count != null ? Number(health.resolved_count) : null,
    kill_phase: ks.phase ? String(ks.phase) : null,
    kill_blocked: !!ks.stakes_blocked,
    diagnosis: diag.label_ru || diag.label || null,
  }
}

export function opsUnitForHorizon(hz) {
  return unitForHorizon(hz)
}
